/**
 * ログインページ用デバッグ情報コンポーネント
 *
 * 開発環境・ステージング環境でのみ表示される環境情報パネル
 * 接続先データベース、環境変数の設定状況を視覚的に確認できる
 */

import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import {
  getEnvironmentInfo,
  getSupabaseConfig,
  isSupabaseAvailable,
} from '../../utils/environment';

interface LoginDebugInfoProps {
  /** 強制的に表示するか（テスト用） */
  forceShow?: boolean;
}

const LoginDebugInfo: React.FC<LoginDebugInfoProps> = ({ forceShow = false }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // 本番ビルドでは表示しない
  const shouldShow = forceShow || import.meta.env.MODE !== 'production';

  if (!shouldShow) {
    return null;
  }

  const envInfo = getEnvironmentInfo();
  const supabaseConfig = getSupabaseConfig();
  const isSupabaseOk = isSupabaseAvailable();

  // Supabase URLからプロジェクトIDを抽出
  const extractProjectId = (url?: string): string => {
    if (!url) return 'なし';
    const match = url.match(/https:\/\/([a-z0-9-]+)\.supabase\.co/);
    return match ? match[1] : 'unknown';
  };

  const projectId = extractProjectId(supabaseConfig.url);

  // 環境タイプに応じた色設定
  const getColorClasses = () => {
    switch (envInfo.type) {
      case 'staging':
        return {
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          border: 'border-blue-200 dark:border-blue-800',
          text: 'text-blue-800 dark:text-blue-200',
          icon: 'text-blue-600 dark:text-blue-400',
        };
      case 'production':
        return {
          bg: 'bg-green-50 dark:bg-green-900/20',
          border: 'border-green-200 dark:border-green-800',
          text: 'text-green-800 dark:text-green-200',
          icon: 'text-green-600 dark:text-green-400',
        };
      case 'demo':
        return {
          bg: 'bg-orange-50 dark:bg-orange-900/20',
          border: 'border-orange-200 dark:border-orange-800',
          text: 'text-orange-800 dark:text-orange-200',
          icon: 'text-orange-600 dark:text-orange-400',
        };
      case 'development':
        return {
          bg: 'bg-purple-50 dark:bg-purple-900/20',
          border: 'border-purple-200 dark:border-purple-800',
          text: 'text-purple-800 dark:text-purple-200',
          icon: 'text-purple-600 dark:text-purple-400',
        };
      default:
        return {
          bg: 'bg-gray-50 dark:bg-gray-800',
          border: 'border-gray-200 dark:border-gray-700',
          text: 'text-gray-800 dark:text-gray-200',
          icon: 'text-gray-600 dark:text-gray-400',
        };
    }
  };

  const colors = getColorClasses();

  return (
    <div className={`mt-6 rounded-lg border ${colors.bg} ${colors.border} overflow-hidden`}>
      {/* ヘッダー */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full px-4 py-3 flex items-center justify-between ${colors.text} hover:opacity-80 transition-opacity`}
      >
        <div className='flex items-center space-x-2'>
          <span className='text-lg'>{envInfo.icon}</span>
          <span className='font-semibold text-sm'>環境デバッグ情報</span>
        </div>
        {isExpanded ? (
          <ChevronUpIcon className='h-5 w-5' />
        ) : (
          <ChevronDownIcon className='h-5 w-5' />
        )}
      </button>

      {/* コンテンツ */}
      {isExpanded && (
        <div className={`px-4 pb-4 space-y-2 text-xs ${colors.text}`}>
          <div className='grid grid-cols-2 gap-x-4 gap-y-1'>
            {/* APP_MODE */}
            <div className='font-medium'>APP_MODE:</div>
            <div className='font-mono'>{import.meta.env.MODE}</div>

            {/* 環境タイプ */}
            <div className='font-medium'>環境タイプ:</div>
            <div className='font-mono'>{import.meta.env.VITE_ENVIRONMENT_TYPE || '未設定'}</div>

            {/* デモモード */}
            <div className='font-medium'>デモモード:</div>
            <div className='font-mono'>
              {import.meta.env.VITE_DEMO_MODE === 'true' ? '✅ 有効' : '❌ 無効'}
            </div>

            {/* デバッグモード */}
            <div className='font-medium'>デバッグモード:</div>
            <div className='font-mono'>
              {import.meta.env.VITE_DEBUG_MODE === 'true' ? '✅ 有効' : '❌ 無効'}
            </div>
          </div>

          {/* 区切り線 */}
          <div className={`border-t ${colors.border} my-2`}></div>

          {/* 接続先データベース */}
          <div>
            <div className='font-semibold mb-1'>接続先データベース:</div>
            <div className='ml-2 space-y-1'>
              <div className='flex items-center space-x-2'>
                <span className='font-medium'>環境:</span>
                <span className={`font-mono ${colors.icon}`}>
                  {envInfo.label} {envInfo.icon}
                </span>
              </div>
              <div className='flex items-center space-x-2'>
                <span className='font-medium'>プロジェクトID:</span>
                <span className='font-mono'>{projectId}</span>
              </div>
              <div className='flex items-center space-x-2'>
                <span className='font-medium'>接続状態:</span>
                <span
                  className={`font-mono ${isSupabaseOk ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
                >
                  {isSupabaseOk ? '✅ 利用可能' : '❌ スタブ実装使用'}
                </span>
              </div>
              {supabaseConfig.url && (
                <div className='flex items-center space-x-2'>
                  <span className='font-medium'>URL:</span>
                  <span className='font-mono text-xs break-all'>
                    {supabaseConfig.url.substring(0, 30)}...
                  </span>
                </div>
              )}
              {supabaseConfig.anonKey && (
                <div className='flex items-center space-x-2'>
                  <span className='font-medium'>Anon Key:</span>
                  <span className='font-mono'>
                    {supabaseConfig.anonKey.substring(0, 20)}... ({supabaseConfig.anonKey.length}
                    文字)
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* 警告メッセージ */}
          {!isSupabaseOk && (
            <div className='mt-2 p-2 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-red-800 dark:text-red-200'>
              <div className='font-semibold'>⚠️ Supabase接続エラー</div>
              <div className='text-xs mt-1'>
                環境変数 VITE_SUPABASE_URL と VITE_SUPABASE_ANON_KEY を確認してください。
              </div>
            </div>
          )}

          {/* ヘルプテキスト */}
          <div className='mt-3 pt-2 border-t border-gray-300 dark:border-gray-600 text-xs opacity-70'>
            💡 この情報は開発環境でのみ表示されます
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginDebugInfo;
